import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AnalyzeFilters, UserFilter } from '../../analyze-filters';
import { MessageService } from 'primeng/api';
import { UserService } from '@/core/services/open-api/user.service';
import { firstValueFrom, lastValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReportSettings } from '@/core/models/report-settings.interface';
import { DefaultDateFormatListService } from '@/core/services/default-date-format-list.service';
import { Layer } from '@/core/models/layer';
import { GroupByOptions, RoundingConfig } from '@/core/models/report';
import { InvoiceRequestForm, InvoiceResponseForm, ResourceTypes } from '@/core/models/integration-types';
import { DateRangeHelper } from '@/core/services/utils/date-range-helper';
import { DateUtilsService } from '@/core/services/utils/date-utils.service';
import { AvailableIntegrations, Contact } from '@/core/models/IntegrationEntity';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { ReauthError } from '@/core/types/reauth-error';
import { ToastService } from '@/core/services/toast.service';
import { TileService } from '@/core/services/open-api/tile.service';
import { Tile } from '@/core/models/tile';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { getMissingUnitsKey, TempStoreService } from '@/core/services/temp-store.service';
import { IntegrationUtils } from '@/core/utils/integration-utils';

const NOTE_OPTION_VALUE = 'notes';

type ContactOption = {
    label: string;
    value: string;
};

@Component({
    selector: 'app-create-invoice',
    templateUrl: './create-invoice.component.html',
    styleUrls: ['./create-invoice.component.scss'],
    standalone: false,
})
export class CreateInvoiceComponent implements OnInit, OnDestroy {
    @Input({ required: true }) serviceIdentifier!: AvailableIntegrations;
    @Input() reportSettings: ReportSettings | null = null;
    @Input() allUsers: UserFilter[] = [];
    @Input({ required: true }) filters!: AnalyzeFilters;
    @Input() layers: Layer[] = [];
    @Input() invoiceTotalTime: string = '';
    @Output() activitiesRefreshRequested = new EventEmitter<void>();
    @Input() activityIds: number[] | undefined = undefined;
    showInvoiceDialog: boolean = false;
    isInvoiceLoading: boolean = false;
    selectedGroupingOption: {
        label: string | undefined;
        value: number | string | undefined;
    } | null = null;
    selectedContact: ContactOption | null = null;
    autoSelectContact: ContactOption | undefined = undefined;
    selectedContactObj?: Contact;
    contactDropdownOptions: ContactOption[] = []; // Dropdown options for contacts
    groupingOptions: {
        label: string | undefined;
        value: number | string | undefined;
    }[] = [];
    defaultGroupingOption = { label: 'Notes', value: NOTE_OPTION_VALUE };
    // Selected values
    isLoadingContacts: boolean = false;
    invoiceStartDate: string = new Date().toISOString();
    invoiceEndDate: string = new Date().toISOString();
    contactsOrignal: Contact[] = [];
    defaultDateFormat: string;
    needsReauth: boolean = false;
    includeNotes: boolean = false;
    hourlyRate: number | null = null;
    markBilled: boolean = false;
    currentUserLocale: string = 'en-US';
    isAdmin: boolean = false;
    missingUnits: string[] | undefined = undefined;
    private readonly destroy$ = new Subject<void>();
    private tiles?: Tile[];

    get isInvoiceSaveDisabled(): boolean {
        return (
            this.needsReauth ||
            !this.selectedGroupingOption ||
            !this.selectedContact ||
            !this.invoiceStartDate ||
            !this.invoiceEndDate ||
            !!this.selectedContactObj?.flags?.noAddr
        );
    }

    constructor(
        public readonly messageService: MessageService,
        private readonly toastService: ToastService,
        public readonly userService: UserService,
        private readonly dateUtilsService: DateUtilsService,
        private readonly integrationService: IntegrationService,
        private readonly tileService: TileService,
        private readonly translate: TranslateService,
        private readonly router: Router,
        private readonly tempStore: TempStoreService,
    ) {
        this.defaultDateFormat = DefaultDateFormatListService.getDefaultDateFormat();
    }

    async ngOnInit(): Promise<void> {
        this.tiles = await this.tileService.getTiles();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onContactChange(event: { value: { value: string } }): void {
        const contactId = event.value.value ?? null;
        this.selectContact(contactId);
    }

    private selectContact(contactId: string): void {
        this.selectedContactObj = this.contactsOrignal.find((c) => c.id === contactId);
    }

    openCreateInvoiceModal = async (): Promise<void> => {
        const startDate = this.filters.dateRange[0];
        const endDate = DateRangeHelper.getEndNotNull(this.filters.dateRange);
        const currentUser = await this.userService.getCurrentUser();
        const userDateFormat = currentUser.settings.dateFormat || this.defaultDateFormat;

        this.currentUserLocale = currentUser.getLocale();
        this.isAdmin = currentUser.isAdmin();
        this.missingUnits = undefined;

        if (this.reportSettings !== null) {
            this.invoiceStartDate = this.dateUtilsService.formatDate(startDate, userDateFormat);
            this.invoiceEndDate = this.dateUtilsService.formatDate(endDate, userDateFormat);

            this.defaultGroupingOption.label = this.translate.instant('group_by_notes');
            this.groupingOptions = [
                this.layers
                    .filter((layer) => layer.visible)
                    .map((layer) => ({
                        label: layer.name,
                        value: layer.id,
                    })),
                this.defaultGroupingOption,
            ].flat();
        }

        this.showInvoiceDialog = true;
        await this.fetchContacts();
    };

    // Fetch contacts from API and populate dropdown
    async fetchContacts(): Promise<void> {
        this.isLoadingContacts = true;
        try {
            const response: Contact[] = await lastValueFrom(
                this.integrationService.getCustomersForInvoice(this.serviceIdentifier),
            );
            if (response && response.length > 0) {
                this.contactsOrignal = response;
                this.contactDropdownOptions = response
                    .map((contact: Contact) => ({
                        label: contact.name,
                        value: contact.id,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.needsReauth = false;
                if (this.selectedContact) {
                    this.selectContact(this.selectedContact.value);
                } else {
                    const layerFilters = [
                        ...(this.filters.selectedLayer1Filter ?? []),
                        ...(this.filters.selectedLayer2Filter ?? []),
                        ...(this.filters.selectedLayer3Filter ?? []),
                    ];
                    if (this.tiles && layerFilters.length) {
                        let autoSelectContact: ContactOption | undefined = undefined;
                        for (const lf of layerFilters) {
                            const tile = this.tiles.find((t) => t.id === lf.id);
                            const extRef = tile?.externalRefs?.[this.serviceIdentifier];
                            if (extRef && extRef.type === ResourceTypes.CONTACTS) {
                                const option = this.contactDropdownOptions.find((o) => o.value === extRef.id);
                                if (option) {
                                    if (autoSelectContact) {
                                        // do not auto select a value if there are mutliple matches.
                                        autoSelectContact = undefined;
                                        break;
                                    }
                                    autoSelectContact = option;
                                }
                            }
                        }
                        this.autoSelectContact = autoSelectContact;
                        if (autoSelectContact) {
                            this.selectedContact = autoSelectContact;
                            this.selectContact(this.selectedContact.value);
                        }
                    }
                }
            }
        } catch (error) {
            if (error instanceof ReauthError) {
                this.needsReauth = true;
            }
        } finally {
            this.isLoadingContacts = false;
        }
    }

    // Method to handle modal cancellation
    onCancelInvoice(): void {
        this.showInvoiceDialog = false;
        this.selectedContact = null;
        this.selectedGroupingOption = null;
        this.isInvoiceLoading = false;
        this.hourlyRate = null;
        this.includeNotes = false;
        this.markBilled = false;
        this.selectedContactObj = undefined;
    }

    showInvoiceToast(response: InvoiceResponseForm): void {
        this.messageService.add({
            key: 'invoice',
            summary: 'int_invoice_created',
            severity: 'success',
            detail: response.invoiceLink,
            sticky: true,
        });
    }

    // Save invoice settings
    saveInvoiceSettings(): void {
        if (this.isInvoiceSaveDisabled) {
            return;
        }

        this.isInvoiceLoading = true;

        let userIds =
            this.filters.selectedTeamFilter?.filter((cat) => cat?.type === 'members').map((cat) => cat.id) ?? [];
        if (!userIds || userIds.length === 0) {
            userIds = this.allUsers.map((u) => u.id);
        }

        // Grouping
        let groupBy: GroupByOptions[] = [];
        if (this.selectedGroupingOption?.value === this.defaultGroupingOption.value) {
            groupBy = ['note'];
        } else {
            // Find the layer index (1,2,3) for the selected grouping option
            const layer = this.layers.find((l) => l.id === this.selectedGroupingOption?.value);
            if (layer && layer.index) {
                groupBy = [`layer:${layer.index}` as GroupByOptions];
            }
        }
        // Tiles
        const tileIds: number[] = [
            ...this.filters.selectedLayer1Filter.map((l) => l.id),
            ...this.filters.selectedLayer2Filter.map((l) => l.id),
            ...this.filters.selectedLayer3Filter.map((l) => l.id),
        ];

        // Groups
        const groupIds: number[] =
            this.filters.selectedTeamFilter?.filter((cat) => cat?.type === 'groups').map((cat) => cat.id) ?? [];

        // Note filter
        let note: string | undefined = undefined;
        let hasNote: boolean | undefined = undefined;
        if (this.filters.isNoteAvailable !== null && this.filters.isNoteAvailable !== undefined) {
            hasNote = this.filters.isNoteAvailable;
        } else if (this.filters.filterDescription) {
            note = this.filters.filterDescription;
        }

        if (!this.selectedContact?.value) {
            console.error(
                `Error creating invoice for account service "${this.serviceIdentifier}: Contact ID must not be empty!`,
            );
            this.toastService.error('standard_error', 'int_invoice_creation_error');
            this.isInvoiceLoading = false;
            return;
        }

        const rounding = this.buildRoundingConfig();

        // TODO the following hotfix has to be properly reviewed.
        //      the old code logic had an issue and send the ISO date to the API
        //      so that the day ended at 21:59:59 instead of 23:59:59
        //      for some reason the invoice generation endpoint treated it as local and not as UTC time.
        //      This caused that time entries which ended after 22:00 not to occur in the invoice,
        //      even if they have been listed in the table.
        const localStartDate = DateRangeHelper.getStartDate(this.filters.dateRange);
        const localEndDate = DateRangeHelper.getEndDate(this.filters.dateRange);

        const startDate = this.dateUtilsService.getLocalTimeIso(localStartDate, true);
        const endDate = this.dateUtilsService.getLocalTimeIso(localEndDate, true);

        const invoiceReq: InvoiceRequestForm = {
            reportRequest: {
                reports: [
                    {
                        utcOffset: 'local',
                        groupBy: groupBy,
                        format: {
                            user: 'default',
                            includeNotes: this.includeNotes,
                            ...(rounding && { rounding }),
                            // this instructs the report reponse to include activity IDs for each grouping such that i can be used
                            // to bulk update the billed status of activities included in the invoice
                            includeActivityIds: true,
                        },
                        filter: {
                            startDate,
                            endDate,
                            userIds: userIds,
                            tileIds: tileIds.length > 0 ? tileIds : undefined,
                            billed: this.filters.isBilled,
                            groupIds: groupIds.length > 0 ? groupIds : undefined,
                            note: note,
                            hasNote: hasNote,
                        },
                    },
                ],
            },
            hourlyRate: this.hourlyRate ?? undefined,
            markBilled: this.markBilled,
            contactId: this.selectedContact!.value,
        };

        if (this.activityIds) {
            invoiceReq.reportRequest.reports[0].filter.activityIds = this.activityIds;
        }

        this.integrationService
            .createInvoice(this.serviceIdentifier, invoiceReq)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: InvoiceResponseForm) => {
                    // if markBilled is true, refresh the activities so that the billed status is updated
                    if (this.markBilled) {
                        this.activitiesRefreshRequested.emit();
                    }
                    this.showInvoiceToast(response);
                    this.isInvoiceLoading = false;
                    this.showInvoiceDialog = false;
                    // clean up
                    this.selectedContact = null;
                    this.selectedGroupingOption = null;
                    this.needsReauth = false;
                },
                error: (error) => {
                    if (error instanceof ReauthError) {
                        this.needsReauth = true;
                    }
                    console.error(`Error creating invoice for accounting service "${this.serviceIdentifier}:`, error);
                    this.toastService.error('standard_error', 'int_invoice_creation_error');
                    this.isInvoiceLoading = false;
                },
            });
    }

    private buildRoundingConfig(): RoundingConfig | undefined {
        if (!this.reportSettings) {
            return undefined;
        }

        const { roundingEnabled, durationRoundingUnit, durationRoundingType } = this.reportSettings;

        const roundingIsActive = roundingEnabled && durationRoundingType && durationRoundingType !== 'none';
        if (!roundingIsActive) {
            return undefined;
        }

        const unit = this.normalizeRoundingUnit(durationRoundingUnit);
        if (!unit) {
            return undefined;
        }

        return { unit, type: durationRoundingType };
    }

    /** Normalizes the rounding unit to an integer. Accepts number or string inputs (e.g. "15" → 15). Returns null if the value cannot be parsed. */
    private normalizeRoundingUnit(value: number | string | null): number | null {
        // Guard null explicitly: Number(null) === 0, which would be valid-looking but incorrect handling.
        // Guard number: already the correct type, no conversion needed.
        if (value === null || typeof value === 'number') {
            return value;
        }
        const parsed = Number(value);
        return isNaN(parsed) ? null : parsed;
    }

    public async onGroupingOptionChange(selectedOption: { label: string; value: string }): Promise<void> {
        if (selectedOption.value === NOTE_OPTION_VALUE) {
            return;
        }
        if (!this.activityIds) {
            console.warn('CreateInvoiceComponent: activityIds not defined - skipping unit mapping check.');
            return;
        }
        const layerId = Number(selectedOption.value);
        const layer = this.layers.find((l) => l.id === layerId);
        if (!layer) {
            console.error(`INTERNAL ERROR: Layer with ID ${layerId} not found.`);
            return;
        }
        const layerIndex = layer.index!;
        const resp = await firstValueFrom(
            this.integrationService.checkUnitMappingUsingActivities(
                this.serviceIdentifier,
                layerIndex,
                this.activityIds,
            ),
        );
        if (resp.success) {
            this.missingUnits = undefined;
        } else {
            this.missingUnits = resp.details.missingUnits;
        }
    }

    public openManageUnits(): void {
        const storeKey = getMissingUnitsKey(this.serviceIdentifier);
        this.tempStore.set(storeKey, this.missingUnits);
        void this.router.navigate([IntegrationUtils.getDetailsPageLink(this.serviceIdentifier)]);
    }
}
